import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface Props {
  onToast: (type: "success" | "error", message: string) => void;
}

export default function WaitingStateManager({ onToast }: Props) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("upcoming_events_waiting_state")
      .select("title, message")
      .eq("id", 1)
      .maybeSingle();

    if (!error && data) {
      setTitle(data.title || "");
      setMessage(data.message || "");
    }
    setLoading(false);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      onToast("error", "O título do aviso não pode ficar vazio.");
      return;
    }

    setSaving(true);
    const { error } = await supabase
      .from("upcoming_events_waiting_state")
      .upsert({
        id: 1,
        title: title.trim(),
        message: message.trim(),
        updated_at: new Date().toISOString(),
      });

    setSaving(false);
    if (error) {
      onToast("error", "Falha ao salvar o aviso.");
    } else {
      onToast("success", "Aviso de espera atualizado!");
    }
  }

  return (
    <div className="bg-white rounded-2xl p-5 md:p-6 mb-8">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <i className="ri-chat-heart-line text-primary" />
        </div>
        <div>
          <h4 className="font-display text-base text-brown">Card de espera (sem eventos futuros)</h4>
          <p className="font-body text-xs text-brown/60 mt-0.5">
            Este aviso aparece na home quando não há nenhum encontro futuro ativo.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 py-4">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="font-body text-sm text-brown/60">Carregando...</p>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="font-body text-sm text-brown block mb-2">Título</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
              placeholder="Em breve, novos encontros"
            />
          </div>
          <div>
            <label className="font-body text-sm text-brown block mb-2">Texto</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors resize-none"
              placeholder="Texto acolhedor para o aviso..."
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {saving ? "Salvando..." : "Salvar aviso"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}